import { ButtonColor, ButtonSize, Container, ContainerSize, Dropdown, DropdownDirection, Gap, Heading, Stack, Text } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type DropDownProps = {}

const DropdownPage = ({ }: DropDownProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Dropdown
            </Heading>

            <Text el="p">The <Text el="strong">Dropdown</Text> component is used to toggle a menu of actions or options
                from a clickable trigger.
                It helps organize multiple choices in a compact and accessible way.</Text>


            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Example
            </Heading>
            <Text el="p">The simplest form of a <Text el="strong">dropdown</Text> consists of a toggle button and a menu
                containing selectable items. When the
                toggle is clicked, the menu appears and displays the list of options.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Dropdown>
                        <Dropdown.Toggle>Dropdown Button</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownBasicCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Appearance
            </Heading>
            <Text el="p">You can render dropdown items as <Text el="strong">buttons</Text> or <Text
                el="strong">links</Text> using the <Text el="strong">el prop</Text>. Use <Text
                    el="strong">el="button"</Text> when you
                want to attach an <Text el="strong">onClick handler</Text> for custom actions, or use <Text
                    el="strong">el="a"</Text> with <Text el="strong">href</Text> when the item should
                behave like a link.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Dropdown>
                        <Dropdown.Toggle>Dropdown Button</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="button">Action</Dropdown.Item>
                            <Dropdown.Item el="button">Another action</Dropdown.Item>
                            <Dropdown.Item el="button">Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownWithButtonCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Color variant
            </Heading>
            <Text el="p">You can apply different visual styles to the dropdown toggle using the <Text
                el="strong">color</Text> prop. This allows the button to match the theme or purpose of your
                interface.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Primary}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Success}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Info}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Warning}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Danger}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownColorCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Split button
            </Heading>
            <Text el="p">A <Text el="strong">split dropdown</Text> separates the main action from the toggle arrow. This
                allows users to quickly
                trigger a primary action while still accessing additional options from the dropdown menu.The <Text
                    el="strong">split prop</Text> sets to true which separates the main action from the dropdown toggle.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Primary} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Success} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Info} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Warning} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Danger} split={true}>Dropdown Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownSplitButtonCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Sizing
            </Heading>
            <Text el="p">The <Text el="strong">size prop</Text> allows you to adjust the overall size of the dropdown
                toggle button. This is
                useful when you need small, medium, or large buttons to match your UI design.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary} size={ButtonSize.Lg}>Dropdown
                                Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary} split={true} size={ButtonSize.Lg}>Dropdown
                                Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownLGButtonCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary} size={ButtonSize.Sm}>Dropdown
                                Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Toggle color={ButtonColor.Secondary} split={true} size={ButtonSize.Sm}>Dropdown
                                Button</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownSMButtonCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Menu alignment
            </Heading>
            <Text el="p">You can control the horizontal alignment of a dropdown menu using the <Text
                el="strong">direction</Text> prop. This
                determines where the menu appears relative to the toggle button.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap3}>
                        <Dropdown direction={DropdownDirection.DropDownStart}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu starting from
                                left</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown direction={DropdownDirection.DropDownCenter}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu in center</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown direction={DropdownDirection.DropDownEnd}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu starting from
                                right</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropdownMenuPositionCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap3}>
                        <Dropdown direction={DropdownDirection.DropUpStart}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropup menu starting from
                                Start</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown direction={DropdownDirection.DropUpCenter}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropup menu in center</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown direction={DropdownDirection.DropUpEnd}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdup menu from end</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropUpMenuPositionCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap3}>
                        <Dropdown direction={DropdownDirection.DropStart}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Drop menu starting from
                                Start</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Dropdown direction={DropdownDirection.DropEnd}>
                            <Dropdown.Toggle color={ButtonColor.Secondary}>Drop menu starting from end</Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
                                <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DropStartEndPositionCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Active
            </Heading>
            <Text el="p">You can mark a dropdown item as <Text el="strong">active</Text> using the <Text el="strong">active
                prop</Text></Text>
            <CodeSnippet>
                <CodePreview>
                    <Dropdown>
                        <Dropdown.Toggle color={ButtonColor.Secondary}>Active menu</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#" active={true}>Another action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {ActiveDropdownCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Disabled
            </Heading>
            <Text el="p">You can disable any item in the dropdown using the <Text el="strong">disabled prop</Text>. This
                prevents interaction and applies the visual disabled style automatically.</Text>

            <CodeSnippet>
                <CodePreview>
                    <Dropdown>
                        <Dropdown.Toggle color={ButtonColor.Secondary}>Disabled menu</Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#" disabled={true}>Another action</Dropdown.Item>
                            <Dropdown.Item el="a" href="#" disabled={true}>Something else here</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {DisabledDropdownCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default DropdownPage;

const DropdownBasicCode = `<Dropdown>
    <Dropdown.Toggle>Dropdown Button</Dropdown.Toggle>
    <Dropdown.Menu>
        <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
        <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
        <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
    </Dropdown.Menu>
</Dropdown>`;

const DropdownWithButtonCode = `<Dropdown>
    <Dropdown.Toggle>Dropdown Button</Dropdown.Toggle>
    <Dropdown.Menu>
        <Dropdown.Item el="button">Action</Dropdown.Item>
        <Dropdown.Item el="button" >Another action</Dropdown.Item>
        <Dropdown.Item el="button" >Something else here</Dropdown.Item>
    </Dropdown.Menu>
</Dropdown>`;

const DropdownColorCode = `<Dropdown>
        <Dropdown.Toggle color={ButtonColor.Primary}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>`;

const DropdownSplitButtonCode = `<Dropdown>
    <Dropdown.Toggle color={ButtonColor.Primary} split={true}>Dropdown Button</Dropdown.Toggle>
    <Dropdown.Menu>
        <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
        <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
        <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
    </Dropdown.Menu>
</Dropdown>`;

const DropdownLGButtonCode = `<Stack gap={Gap.Gap2}>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Secondary} size={ButtonSize.Lg}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Secondary} split={true} size={ButtonSize.Lg}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const DropdownSMButtonCode = `<Stack gap={Gap.Gap2}>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Secondary} size={ButtonSize.Sm}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
    <Dropdown>
        <Dropdown.Toggle color={ButtonColor.Secondary} split={true} size={ButtonSize.Sm}>Dropdown Button</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const DropdownMenuPositionCode = `<Stack gap={Gap.Gap3}>
    <Dropdown direction={DropdownDirection.DropDownStart}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu starting from left</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>

    <Dropdown direction={DropdownDirection.DropDownCenter}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu in center</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>

    <Dropdown direction={DropdownDirection.DropDownEnd}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdown menu starting from right</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const DropUpMenuPositionCode = `<Stack gap={Gap.Gap3}>
    <Dropdown direction={DropdownDirection.DropUpStart}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropup menu starting from
            Start</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>

    <Dropdown direction={DropdownDirection.DropUpCenter}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropup menu in center</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>

    <Dropdown direction={DropdownDirection.DropUpEnd}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Dropdup menu from end</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const DropStartEndPositionCode = `<Stack gap={Gap.Gap3}>
    <Dropdown direction={DropdownDirection.DropStart}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Drop menu starting from
            Start</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>

    <Dropdown direction={DropdownDirection.DropEnd}>
        <Dropdown.Toggle color={ButtonColor.Secondary}>Drop menu starting from end</Dropdown.Toggle>
        <Dropdown.Menu>
            <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Another action</Dropdown.Item>
            <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
        </Dropdown.Menu>
    </Dropdown>
</Stack>`;

const ActiveDropdownCode = `<Dropdown>
    <Dropdown.Toggle color={ButtonColor.Secondary}>Active menu</Dropdown.Toggle>
    <Dropdown.Menu>
        <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
        <Dropdown.Item el="a" href="#" active={true}>Another action</Dropdown.Item>
        <Dropdown.Item el="a" href="#">Something else here</Dropdown.Item>
    </Dropdown.Menu>
</Dropdown>`;

const DisabledDropdownCode = `<Dropdown>
    <Dropdown.Toggle color={ButtonColor.Secondary}>Disabled menu</Dropdown.Toggle>
    <Dropdown.Menu>
        <Dropdown.Item el="a" href="#">Action</Dropdown.Item>
        <Dropdown.Item el="a" href="#" disabled={true}>Another action</Dropdown.Item>
        <Dropdown.Item el="a" href="#" disabled={true}>Something else here</Dropdown.Item>
    </Dropdown.Menu>
</Dropdown>`;
